import React, { type JSX } from 'react';
import { useConfirm, useDashboardCommonHandlers } from '@fiji/common/src/hooks';
import { FormHelperText, Grid, TextField, Typography } from '@mui/material';
import { CustomModal, ModalActions } from 'components/CustomModal';
import { CustomTransComponent } from 'components';
import { useParams } from 'react-router-dom';
import { Stack } from 'rsuite';
import { AddConfirmationModal } from './AddConfirmationModal';

type ModalProps = {
    widgetID?: string;
    'data-testid'?: string;
    onConfirmCommand?: (data: any, widgetId: string) => void;
};

export const AddConfirmationModalWithInput = React.forwardRef(
    ({ widgetID, 'data-testid': dataTestId, onConfirmCommand }: ModalProps, ref): JSX.Element => {
        const { deviceId } = useParams();
        const [widgetData, setWidgetData] = React.useState<any>(null);
        const { handleDeviceCommands } = useDashboardCommonHandlers(deviceId);
        const detailsModalRef: any = React.useRef(null);
        const maxLength = 5;
        const validRange = 43200;
        const [errorMsg, setErrorMsg] = React.useState<any>(null);

        const { isVisible, onClick, onCancel, onConfirm } = useConfirm(() => {
            if (widgetData?.isConfirm) {
                detailsModalRef?.current?.handleModalAction(true, widgetData);
            } else if (onConfirmCommand) {
                onConfirmCommand(widgetData, widgetID!);
            } else {
                void handleDeviceCommands(widgetData, widgetID);
            }
            onCancel();
        });

        const handleModalAction = (action: boolean, data?: any): void => {
            if (action) {
                setErrorMsg(null);
                setWidgetData(data);
                onClick(data);
                return;
            }
            onCancel();
        };

        React.useImperativeHandle(
            ref,
            () => ({
                handleModalAction: (action: boolean, data: any): void => handleModalAction(action, data),
            }),
            []
        );
        const removeActions: ModalActions[] = [
            {
                testId: 'cancelBtn',
                key: 'cancel',
                label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
                close: true,
                variant: 'outlined',
                handleClick: (): void => {
                    onCancel(true);
                },
            },
            {
                testId: 'commandName',
                key: 'commandName',
                label: <CustomTransComponent translationKey={'WIDGET_MANAGEMENT:APPLY'} />,
                variant: 'contained',
                handleClick: onConfirm,
                disabled: !!errorMsg,
            },
        ];
        const handlePayloadChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
            if (Number(event.target.value) <= validRange && event.target.value.length >= 0) {
                if (event.target.value !== '') {
                    setErrorMsg(null);
                    if (widgetData) {
                        setWidgetData({ ...widgetData, defaultValue: event.target.value });
                    }
                } else {
                    setErrorMsg('WIDGET_MANAGEMENT:WIDGET_MANAGEMENT:VALID_VALUE');
                    setWidgetData({ ...widgetData, defaultValue: '' });
                }
            } else {
                setErrorMsg('WIDGET_MANAGEMENT:WIDGET_MANAGEMENT:VALID_VALUE');
                setWidgetData({ ...widgetData, defaultValue: event.target.value });
            }
        };

        return (
            <>
                <CustomModal
                    type="primary"
                    header={`${widgetData?.displayName}`}
                    isOpen={isVisible}
                    actions={removeActions}
                    noDividers
                    actionsDivider
                    {...(dataTestId && { 'data-testid': dataTestId })}
                >
                    <Typography sx={{ marginLeft: '2px' }}>
                        <CustomTransComponent translationKey={'WIDGET_MANAGEMENT:ENTER_VALUE_TEXT'} />
                    </Typography>
                    {widgetData?.description !== undefined && widgetData?.description !== null && (
                        <Grid container width={'auto'} spacing={2}>
                            <Stack spacing={2}>
                                <Typography
                                    variant="body2"
                                    fontWeight={'500'}
                                    fontSize={'14px'}
                                    sx={{ marginTop: '3%', marginLeft: '4%' }}
                                >
                                    {widgetData?.description?.length <= 300
                                        ? widgetData?.description
                                        : `${widgetData?.description?.substr(0, 300)}...`}
                                </Typography>
                            </Stack>
                        </Grid>
                    )}
                    <TextField
                        className="margin-top-16"
                        id="name"
                        label={<CustomTransComponent translationKey={'WIDGET_MANAGEMENT:DELAY_TIME'} />}
                        variant="filled"
                        fullWidth
                        value={widgetData?.defaultValue}
                        onChange={handlePayloadChange}
                        required
                        inputProps={{ maxLength, max: validRange }}
                        {...(errorMsg && {
                            error: true,
                        })}
                    />
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                        <FormHelperText className="padding-x-10">
                            <CustomTransComponent translationKey={'WIDGET_MANAGEMENT:VALID_VALUE'} /> {widgetData?.unit}
                        </FormHelperText>
                    </Stack>
                </CustomModal>
                <AddConfirmationModal ref={detailsModalRef} widgetID={widgetID} onConfirmCommand={onConfirmCommand} />
            </>
        );
    }
);
AddConfirmationModalWithInput.displayName = 'AddConfirmationModalWithInput';
